<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { ref, onMounted, computed } from "vue";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useDesignationStore } from "@/stores/designationStore";
import EmployeeReportTable from "./employee-report-table.vue";

const title = ref("Employee Report");
const text = ref("HR");
const text1 = ref("Employee Report");

const employeeStore = useEmployeeStore();
const designationStore = useDesignationStore();

const loading = computed(
  () => employeeStore.loading || employeeStore.reportLoading
);
const items = computed(() => employeeStore.items);
const meta = computed(() => employeeStore.meta);
const summary = computed(() => employeeStore.reportSummary);
const employeeChart = computed(() => employeeStore.employeeChart);

const sortLabel = ref("Last 7 Days");
const dateRangeInput = ref(null);

const selectedYearLabel = ref("This Year"); // for year dropdown

// ✅ designation options from store
const designationOptions = computed(() => designationStore.designations || []);

// ✅ label based on selected id
const selectedDesignationLabel = computed(() => {
  const id = employeeStore.filters.designation_id;
  if (!id) return "Designation";
  const found = designationOptions.value.find((d) => d.id === id);
  return found?.name || "Designation";
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function applyFilters(page = 1, options = {}) {
  await Promise.all([
    employeeStore.list({ page, ...(options.force ? { force: true } : {}) }),
    employeeStore.fetchReport(options.force ? { force: true } : {}),
  ]);
}

function handlePageChange(page) {
  applyFilters(page);
}

function setStatus(status) {
  employeeStore.filters.status = status;
  applyFilters(1);
}

// ✅ set by ID now
function setDesignationId(id) {
  employeeStore.filters.designation_id = id || "";
  applyFilters(1);
}

// ✅ Year selector logic (for the "This Year / 2024 / 2023" dropdown)
function setYear(year) {
  let start, end;

  if (year === "this") {
    const now = moment();
    start = now.clone().startOf("year");
    end = now.clone().endOf("year");
    selectedYearLabel.value = "This Year";
  } else {
    start = moment().year(year).startOf("year");
    end = moment().year(year).endOf("year");
    selectedYearLabel.value = String(year);
  }

  employeeStore.filters.date_from = start.format("YYYY-MM-DD");
  employeeStore.filters.date_to = end.format("YYYY-MM-DD");

  if (dateRangeInput.value) {
    dateRangeInput.value.value =
      start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
  }

  applyFilters(1);
}

function setSort(sort, label) {
  employeeStore.filters.sort = sort;
  sortLabel.value = label;
  applyFilters(1);
}

function exportAs(format) {
  employeeStore.downloadReport(format);
}

// chart options based on summary chart
const chartOptions = computed(() => ({
  chart: { type: "bar" },
  xaxis: { categories: employeeChart.value.labels || ["Active", "Inactive"] },
  legend: { position: "bottom" },
}));

const chartSeries = computed(() => [
  {
    name: "Employees",
    data: employeeChart.value.series || [0, 0],
  },
]);

onMounted(async () => {
  // ✅ load designations once
  if (!designationStore.designations.length) {
    await designationStore.ensureDesignations();
  }

  const start = moment().subtract(6, "days");
  const end = moment();

  employeeStore.filters.date_from = start.format("YYYY-MM-DD");
  employeeStore.filters.date_to = end.format("YYYY-MM-DD");

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      function booking_range(start, end) {
        if (dateRangeInput.value) {
          dateRangeInput.value.value =
            start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        }
        employeeStore.filters.date_from = start.format("YYYY-MM-DD");
        employeeStore.filters.date_to = end.format("YYYY-MM-DD");
        applyFilters(1);
      }
    );

    dateRangeInput.value.value =
      start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
  }

  await applyFilters(1);
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('xlsx')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid">
        <!-- Summary cards -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill">
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="overflow-hidden d-flex mb-2 align-items-center">
                    <span class="me-2"
                      ><img
                        src="@/assets/img/reports-img/employee-report-icon.svg"
                        alt="Img"
                        class="img-fluid"
                    /></span>
                    <div>
                      <p class="fs-14 fw-normal mb-1 text-truncate">
                        Total Employees
                      </p>
                      <h5>{{ summary.total_employees }}</h5>
                    </div>
                  </div>
                  <div>
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      Employees within selected filters
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="overflow-hidden d-flex mb-2 align-items-center">
                    <span class="me-2"
                      ><img
                        src="@/assets/img/reports-img/employee-report-success.svg"
                        alt="Img"
                        class="img-fluid"
                    /></span>
                    <div>
                      <p class="fs-14 fw-normal mb-1 text-truncate">
                        Active Employees
                      </p>
                      <h5>{{ summary.active_employees }}</h5>
                    </div>
                  </div>
                  <div>
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      Currently active employees
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="overflow-hidden d-flex mb-2 align-items-center">
                    <span class="me-2"
                      ><img
                        src="@/assets/img/reports-img/employee-report-info.svg"
                        alt="Img"
                        class="img-fluid"
                    /></span>
                    <div>
                      <p class="fs-14 fw-normal mb-1 text-truncate">
                        New Employees
                      </p>
                      <h5>{{ summary.new_employees }}</h5>
                    </div>
                  </div>
                  <div>
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      Joined in the last 30 days
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div class="overflow-hidden d-flex mb-2 align-items-center">
                    <span class="me-2"
                      ><img
                        src="@/assets/img/reports-img/employee-report-danger.svg"
                        alt="Img"
                        class="img-fluid"
                    /></span>
                    <div>
                      <p class="fs-14 fw-normal mb-1 text-truncate">
                        Inactive Employees
                      </p>
                      <h5>{{ summary.inactive_employees }}</h5>
                    </div>
                  </div>
                  <div>
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      Employees marked as inactive
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart -->
        <!-- Chart -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center row-gap-2"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-bar text-danger"></i
                  ></span>
                  <h5>Employees</h5>
                </div>
                <div class="d-flex align-items-center">
                  <p class="d-inline-flex align-items-center me-3 mb-0">
                    <i class="ti ti-square-filled fs-12 text-success me-2"></i>
                    Active Employees
                  </p>
                  <p class="d-inline-flex align-items-center mb-0">
                    <i class="ti ti-square-filled fs-12 text-gray-1 me-2"></i>
                    Inactive Employees
                  </p>
                </div>

                <!-- ✅ Year selector -->
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYearLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear('this')"
                      >
                        This Year
                      </a>
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(2024)"
                      >
                        2024
                      </a>
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(2023)"
                      >
                        2023
                      </a>
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(2022)"
                      >
                        2022
                      </a>
                    </li>
                  </ul>
                </div>
                <!-- /Year selector -->
              </div>
            </div>
            <div class="card-body py-0">
              <div id="employee-reports">
                <apexchart
                  type="bar"
                  height="180"
                  :options="chartOptions"
                  :series="chartSeries"
                ></apexchart>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee List -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Employees List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Designation filter -->
            <!-- Designation filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ selectedDesignationLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setDesignationId('')"
                  >
                    All
                  </a>
                </li>
                <li v-for="d in designationOptions" :key="d.id">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setDesignationId(d.id)"
                  >
                    {{ d.name }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ employeeStore.filters.status || "Select Status" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Active')"
                    >Active</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Inactive')"
                    >Inactive</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('')"
                    >All</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('asc', 'Name (A to Z)')"
                    >Name (A to Z)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('desc', 'Name (Z to A)')"
                    >Name (Z to A)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest', 'Oldest First')"
                    >Oldest First</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <EmployeeReportTable
              :items="items"
              :loading="loading"
              :meta="meta"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  #employee-reports {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


